import React, { useState, useEffect, useRef } from "react";
import ContactForm from "./ContactForm";
import SliderGallery from "./SliderGallery";
import Scroll from "./Scroll";
import Price from "./Price";
import MyWorks from "./MyWorks";
import CommissionTOS from "./CommissionTOS";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./style.css";
import { useLanguage } from "./LanguageProvider";

export default function App() {
  const [submit, setSubmit] = useState(false);
  const { changeLanguage } = useLanguage();
  const [newLanguage, setNewLanguage] = useState("en");
  const [isChange, setIsChange] = useState(true);
  const [currentFrame, setCurrentFrame] = useState(0); // 現在のフレーム
  const [language, setLanguage] = useState("ja"); // 言語状態（JP or EN）

  // 48枚のPNG画像を配列に格納
  const frames = Array.from(
    { length: 48 },
    (_, index) => `language/change/${index}.png`
  );

  // JPの場合のフレーム範囲
  const enRange = [0, 24]; // JPでは1枚目から25枚目（0-indexed）
  // ENの場合のフレーム範囲
  const jpRange = [24, 48]; // ENでは25枚目から48枚目（0-indexed）

  let interval;

  // 言語によって表示するフレーム範囲を変更
  const frameRange = language === "ja" ? jpRange : enRange;

  const currentFrameRef = useRef(frameRange[0]);

  useEffect(() => {
    currentFrameRef.current = frameRange[0];
    setCurrentFrame(frameRange[0]); // すぐに初期フレームを設定

    interval = setInterval(() => {
      if (currentFrameRef.current < frameRange[1] - 1) {
        currentFrameRef.current += 1;
        setCurrentFrame(currentFrameRef.current);
      } else {
        clearInterval(interval);
        setIsChange(false);
      }
    }, 36);
  }, [language]); // 言語が変わるたびに再実行

  // 言語を切り替える
  const handleLanguageChange = (newLanguage) => {
    if (!isChange) {
      setIsChange(true);
      setLanguage(newLanguage);
      setNewLanguage(newLanguage === "ja" ? "en" : "ja");
      changeLanguage(newLanguage);
    }
  };

  return (
    <div className="container">
      <div className="scrollButton">
        <Scroll />
        <button id="toTopBtn">
          <img src="top/top_.png" alt="Top" />
        </button>
        <button
          onClick={() => handleLanguageChange(newLanguage)}
          className="language-switch"
          disabled={submit}
        >
          <img src={frames[currentFrame]} alt="Animated Frame" />
        </button>
      </div>
      <header>
        <h1>Virtual Quokka</h1>
        <div className="social-media">
          <a
            href="https://x.com/virtual_quokka"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-x-twitter">𝕏</i>
          </a>
          <a
            href="https://bsky.app/profile/virtualquokka.bsky.social"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fa-brands fa-bluesky"></i>
          </a>
          <a
            href="https://www.youtube.com/@virtual_quokka"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-youtube"></i>
          </a>
          <a
            href="https://www.twitch.tv/virtual_quokka"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-twitch"></i>
          </a>
          <a href="mailto:virtualquokka@gmail.com">
            <i className="fas fa-envelope"></i>
          </a>
          <a
            href="https://virtual-quokka.booth.pm/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fa-solid fa-cube"></i>
          </a>
          <a
            href="https://www.pixiv.net/users/5666825"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fa-brands fa-pixiv"></i>
          </a>
          <a
            href="https://virtualquokka.fanbox.cc/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fa-solid fa-box-open"></i>
          </a>
          <a
            href="https://www.patreon.com/c/virtual_quokka"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-patreon"></i>
          </a>
        </div>
      </header>
      <div></div>
      <nav>
        <ul className="menu">
          <li>
            <a href="#about">
              <img src="menu/about.gif" alt="About Animation" />
              About
            </a>
          </li>
          <li>
            <a href="#gallery">
              <img src="menu/gallery.gif" alt="Gallery Animation" />
              Gallery
            </a>
          </li>
          <li>
            <a href="#price">
              <img src="menu/price.gif" alt="Price Animation" />
              Price List
            </a>
          </li>
          <li className="menu-item-commission">
            <a href="#commission-tos">
              <img
                src="menu/commission_tos.gif"
                alt="Commission tos Animation"
              />
              Commission
              <br />
              TOS
            </a>
          </li>
          <li>
            <a href="#contact">
              <img src="menu/contact.gif" alt="Contact Animation" />
              Contact
            </a>
          </li>
          <li>
            <a href="#my-works">
              <img src="menu/works.gif" alt="Works Animation" />
              My Works
            </a>
          </li>
        </ul>
      </nav>
      {/* そのほかのセクション */}
      <section id="about">
        <h2>About</h2>
        <div className="about-content">
          <img
            src="about/about.gif"
            alt="Virtual Quokka"
            className="about-image"
          />
          <div className="about-text">
            <div className="about-japanese">
              <h1>はじめまして！バーチャルクオッカです。</h1>
              <p>
                イラストやアニメーションを中心に、かわいいコンテンツをお届けしています。
              </p>
              <p>
                楽しいことが大好きで、皆さんに笑顔を届けられるよう頑張っています！
              </p>
            </div>
            <div className="about-english">
              <h1>Nice to meet you! I'm Virtual Quokka.</h1>
              <p>
                I create cute content, mainly focusing on illustrations and
                animations.
              </p>
              <p>
                I love fun things, and I'm working hard to bring smiles to
                everyone!
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="gallery">
        <h2>Gallery</h2>
        <SliderGallery />
      </section>

      <section id="price">
        <h2>Price List</h2>
        <div className="language-icons">
          {language === "ja" && (
            <button
              onClick={() => handleLanguageChange("en")}
              className="flag-icon flag-icon-us"
              disabled={submit}
            ></button>
          )}
          {language === "en" && (
            <button
              onClick={() => handleLanguageChange("ja")}
              className="flag-icon flag-icon-jp"
              disabled={submit}
            ></button>
          )}
        </div>

        <Price />
      </section>
      <section id="commission-tos">
        <h2>Commission TOS</h2>
        <div className="language-icons">
          {language === "ja" && (
            <button
              onClick={() => handleLanguageChange("en")}
              className="flag-icon flag-icon-us"
              disabled={submit}
            ></button>
          )}
          {language === "en" && (
            <button
              onClick={() => handleLanguageChange("ja")}
              className="flag-icon flag-icon-jp"
              disabled={submit}
            ></button>
          )}
        </div>
        <CommissionTOS />
      </section>

      <section id="contact">
        <div className="language-icons">
          {language === "ja" && (
            <button
              onClick={() => handleLanguageChange("en")}
              className="flag-icon flag-icon-us"
              disabled={submit}
            ></button>
          )}
          {language === "en" && (
            <button
              onClick={() => handleLanguageChange("ja")}
              className="flag-icon flag-icon-jp"
              disabled={submit}
            ></button>
          )}
        </div>
        <h2>Contact</h2>

        <ContactForm language={language} setSubmit={setSubmit} />
      </section>
      <section id="my-works">
        <h2>My Works</h2>
        <MyWorks />
      </section>
    </div>
  );
}
